import axios from "axios";
import { DEFAULT_AXIOS_CONFIG } from "./userService";
import { requireCanonicalUserId } from "./userIdRequestValidation";
import { resolveGatewayBackedServiceBaseUrl } from "./serviceBaseUrl";
import {
    normalizeUserConnectionList,
    normalizeUserConnectionPage,
    withOptionalOffsetPagination,
} from "../utils/userConnectionList";

const USER_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_USER_SERVICE_URL);

const withAuth = (config = {}) => ({ ...DEFAULT_AXIOS_CONFIG, ...config });

const getAcceptedFriends = async () => {
    const response = await axios.get(`${USER_SERVICE_URL}/api/users/me/friends`, withAuth());
    return normalizeUserConnectionList(response.data);
};

const getAcceptedFriendsPage = async (options = {}) => {
    const response = await axios.get(
        withOptionalOffsetPagination(`${USER_SERVICE_URL}/api/users/me/friends`, options),
        withAuth(options)
    );
    return normalizeUserConnectionPage(response.data, options);
};

const getUserFriends = async (userId) => {
    const normalizedUserId = requireCanonicalUserId(userId);
    const response = await axios.get(
        `${USER_SERVICE_URL}/api/users/friends/${encodeURIComponent(normalizedUserId)}`,
        withAuth()
    );
    return normalizeUserConnectionList(response.data);
};

const getUserFriendsPage = async (userId, options = {}) => {
    const normalizedUserId = requireCanonicalUserId(userId);
    const response = await axios.get(
        withOptionalOffsetPagination(`${USER_SERVICE_URL}/api/users/friends/${encodeURIComponent(normalizedUserId)}`, options),
        withAuth(options)
    );
    return normalizeUserConnectionPage(response.data, options);
};

const getIncomingRequests = async () => {
    const response = await axios.get(`${USER_SERVICE_URL}/api/users/me/friends/requests/incoming`, withAuth());
    return Array.isArray(response.data) ? response.data : [];
};

const getIncomingRequestsPage = async (options = {}) => {
    const response = await axios.get(
        withOptionalOffsetPagination(`${USER_SERVICE_URL}/api/users/me/friends/requests/incoming`, options),
        withAuth(options)
    );
    return normalizeUserConnectionPage(response.data, options);
};

const getOutgoingRequests = async () => {
    const response = await axios.get(`${USER_SERVICE_URL}/api/users/me/friends/requests/outgoing`, withAuth());
    return Array.isArray(response.data) ? response.data : [];
};

const getOutgoingRequestsPage = async (options = {}) => {
    const response = await axios.get(
        withOptionalOffsetPagination(`${USER_SERVICE_URL}/api/users/me/friends/requests/outgoing`, options),
        withAuth(options)
    );
    return normalizeUserConnectionPage(response.data, options);
};

const sendFriendRequest = async (friendId) => {
    const normalizedFriendId = requireCanonicalUserId(friendId);
    const response = await axios.post(
        `${USER_SERVICE_URL}/api/users/me/friends/add/${encodeURIComponent(normalizedFriendId)}`,
        null,
        withAuth()
    );
    return response.data;
};

const acceptFriendRequest = async (requestId) => {
    const response = await axios.put(
        `${USER_SERVICE_URL}/api/users/friends/${requestId}/accept`,
        null,
        withAuth()
    );
    return response.data;
};

const rejectFriendRequest = async (requestId) => {
    const response = await axios.put(
        `${USER_SERVICE_URL}/api/users/friends/${requestId}/reject`,
        null,
        withAuth()
    );
    return response.data;
};

const cancelFriendRequest = async (requestId) => {
    const response = await axios.delete(
        `${USER_SERVICE_URL}/api/users/friends/${requestId}/cancel`,
        withAuth()
    );
    return response.data;
};

const removeFriend = async (friendId) => {
    const normalizedFriendId = requireCanonicalUserId(friendId);
    await axios.delete(`${USER_SERVICE_URL}/api/users/me/friends/${encodeURIComponent(normalizedFriendId)}`, withAuth());
};

const friendshipService = {
    getAcceptedFriends,
    getAcceptedFriendsPage,
    getUserFriends,
    getUserFriendsPage,
    getIncomingRequests,
    getIncomingRequestsPage,
    getOutgoingRequests,
    getOutgoingRequestsPage,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    cancelFriendRequest,
    removeFriend,
};

export default friendshipService;
